import { useEffect, type RefObject } from 'react';

// Document title per screen (web accessibility, WCAG 2.4.2; Flutter's
// MaterialApp title is the fixed "Plume", SHELL-025). The title follows what
// the user sees: the page title (`h1`) of the topmost interactive layer, so
// it carries real names ("Orchard Chronicles – Plume") as soon as the screen
// has them, and falls back to that layer's landmark name (the tab or overlay
// label) while it has no heading yet.

export const APP_TITLE = 'Plume';

/** "Library – Plume"; just "Plume" without a screen name. */
export function formatDocumentTitle(screen: string | null | undefined): string {
  const name = screen?.replace(/\s+/g, ' ').trim();
  return name && name !== APP_TITLE ? `${name} – ${APP_TITLE}` : APP_TITLE;
}

function isExposed(element: Element, layer: Element): boolean {
  const hidden = element.closest('[inert], [aria-hidden="true"]');
  return hidden === null || !layer.contains(hidden);
}

/**
 * The name of the screen on top of `root` (the navigator root, whose children
 * are the shell or gate, the desktop chrome, and the pushed pages and modal
 * layers above them):
 * the first exposed `h1` of the last child that is not inert (covered and
 * leaving layers are), else the label of its main landmark.
 */
export function visibleScreenName(root: Element): string | null {
  // The desktop chrome (sidebar, now-playing bar) sits between the layers but is not a screen.
  const layers = Array.from(root.children).filter((child) => !child.hasAttribute('inert') && !child.hasAttribute('data-navigator-chrome'));
  const top = layers[layers.length - 1];
  if (!top) return null;
  for (const heading of Array.from(top.querySelectorAll('h1'))) {
    const text = heading.textContent?.trim();
    if (text && isExposed(heading, top)) return text;
  }
  const landmarks = top.matches('[role="main"]') ? [top] : Array.from(top.querySelectorAll('[role="main"]'));
  for (const landmark of landmarks) {
    const label = landmark.getAttribute('aria-label')?.trim();
    if (label && isExposed(landmark, top)) return label;
  }
  return null;
}

/** Keeps `document.title` on the visible screen's name while `root` is mounted. */
export function useDocumentTitle(rootRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof MutationObserver === 'undefined') return;
    const update = () => {
      const title = formatDocumentTitle(visibleScreenName(root));
      if (document.title !== title) document.title = title;
    };
    // Mutation records arrive batched (once per microtask checkpoint), so one update per batch.
    const observer = new MutationObserver(update);
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['inert', 'aria-hidden', 'role', 'aria-label'],
    });
    update();
    return () => {
      observer.disconnect();
      document.title = APP_TITLE;
    };
  }, [rootRef]);
}
